import { useContext, cloneElement, useCallback, useMemo, type ReactElement, type MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { useParams, generatePath } from 'react-router-dom';
import { hrefObjectToString } from '@/domain/router/helpers';

import ROUTES_PATH from '@/router/routesPathConfig';
import { categoryAllSelector } from '@/domain/categories';
import { useDeleteSearchFavoriteMutation, type SearchFavorite } from '@/domain/documents/queries';

import CompanyWorkspaceContext from '@/pages/company/WorkspaceRoute/CompanyWorkspaceContext';
import useFavorites from '@/pages/company/components/CompanyNavigation/FavoritesMenu/useFavorites';

import RouterLink from '@/components/mui/Router/RouterLink';
import { ListItemIcon } from '@/components/mui/Layouts/components/Drawer';
import { FavoriteListLink } from '@/pages/company/components/CompanyNavigation/FavoritesMenu/StyledComponents';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AvatarMui from '@mui/material/Avatar';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from '@mui/material';

const FAVORITE_TYPES = {
  system: 'system',
  saved: 'saved',
  userTag: 'userTag',
};

const FAVORITE_ICONS: Record<string, ReactElement> = {
  system: <SearchOutlinedIcon />,
  saved: <SavedSearchIcon />,
};

const FavoritesListItem = ({ entry }: { entry: SearchFavorite }) => {
  const categoryAll = useSelector(categoryAllSelector);
  const { companyType } = useContext(CompanyWorkspaceContext);
  const { getUserBySearchLink, isCurrentUser } = useFavorites();
  const theme = useTheme();
  const { companyId, category1 } = useParams();
  const deleteFavorite = useDeleteSearchFavoriteMutation(companyId ?? '');

  const getFavoriteType = useMemo(
    () => (favoriteEntry: SearchFavorite) =>
      favoriteEntry.type === 'saved' ? FAVORITE_TYPES.saved : FAVORITE_TYPES.system,
    [],
  );

  const redirectParams = useMemo(
    () => ({
      companyId: companyId ?? '',
      category1: categoryAll ? categoryAll.id : category1,
    }),
    [companyId, categoryAll, category1],
  );

  const search = useMemo(
    () => (entry.type === 'query' && entry.query ? `${entry.query}&label=${entry.label}` : entry.query),
    [entry],
  );

  const route = useMemo(
    () =>
      companyType === 'confidential' ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE : ROUTES_PATH.COMPANY_WORKSPACE,
    [companyType],
  );

  const user = useMemo(() => getUserBySearchLink(entry.query), [entry, getUserBySearchLink]);

  const isIAm = useMemo(() => (user ? isCurrentUser(user.userId) : false), [user, isCurrentUser]);

  const favoriteType = useMemo(
    () => (user ? FAVORITE_TYPES.userTag : getFavoriteType(entry)),
    [user, entry, getFavoriteType],
  );

  const linkTo = useMemo(
    () =>
      hrefObjectToString({
        pathname: generatePath(route.absolute, redirectParams),
        search,
      }),
    [search, route, redirectParams],
  );

  const onDeleteFavorite = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      if (!entry.ID) return;
      deleteFavorite.mutate({ body: { ID: entry.ID } });
    },
    [deleteFavorite, entry.ID],
  );

  return (
    <FavoriteListLink component={RouterLink} to={linkTo}>
      <ListItemIcon minWidth={24} sx={{ color: 'inherit' }}>
        {user ? (
          <AvatarMui src={user.picture} imgProps={{ loading: 'lazy' }} sx={{ width: 24, height: 24 }} />
        ) : (
          cloneElement(FAVORITE_ICONS[favoriteType], { sx: { fontSize: 20 } })
        )}
      </ListItemIcon>
      <ListItemText disableTypography sx={{ my: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontSize: theme.typography.pxToRem(12), color: 'inherit', fontWeight: isIAm ? '500' : '400' }}
        >
          {user ? user.username : entry.label}
        </Typography>
      </ListItemText>
      {entry.type === 'saved' && (
        <IconButton onClick={onDeleteFavorite} color="inherit" edge="end">
          <CloseOutlinedIcon sx={{ fontSize: 12 }} />
        </IconButton>
      )}
    </FavoriteListLink>
  );
};

export default FavoritesListItem;
